// Express "Add images to a campaign" (Part D): pure rules. No I/O.
//
// The AM drops many images into a running campaign with the SAME copy. Each new ad is one
// creative PAIR: a Feed image (4:5 or 1:1) and an optional Story/Reels image (9:16). Pairs are
// matched by filename stem (kawana-hero_feed.jpg + kawana-hero_story.jpg), then by upload
// order, and every chosen ad set gets one ad per pair.

export type ExpressRole = 'feed' | 'story';

export interface ExpressFile {
  id: string;
  name: string;
  width: number | null;
  height: number | null;
}

export interface ExpressPair {
  /** The Feed file's id (a pair always has a Feed image). */
  id: string;
  stem: string;
  feedId: string;
  storyId: string | null;
}

export interface PairingResult {
  pairs: ExpressPair[];
  /** Story images that matched no Feed image. Assign them by hand or drop them. */
  unassignedStoryIds: string[];
}

const FEED_WORDS = ['feed', '4x5', '4-5', '1x1', 'square', 'portrait'];
const STORY_WORDS = ['story', 'stories', 'reel', 'reels', '9x16', '9-16', 'vertical'];

function stripExt(name: string): string {
  return name.replace(/\.[a-z0-9]{2,5}$/i, '');
}

/** The role named by the file's own suffix (`_feed`, `-story`, ` 9x16` ...), or null. */
export function roleFromName(name: string): ExpressRole | null {
  const base = stripExt(name).toLowerCase();
  const m = base.match(/[\s_.-]+([a-z0-9-]+)$/);
  const tail = m?.[1] ?? '';
  if (STORY_WORDS.includes(tail)) return 'story';
  if (FEED_WORDS.includes(tail)) return 'feed';
  return null;
}

/** 9:16 and taller is a Story image; 4:5 to 1:1 is Feed. Anything else says nothing. */
export function roleFromSize(width: number | null, height: number | null): ExpressRole | null {
  if (!width || !height || width <= 0 || height <= 0) return null;
  const ratio = width / height;
  if (ratio <= 0.62) return 'story';
  if (ratio >= 0.78 && ratio <= 1.05) return 'feed';
  return null;
}

export function roleOf(file: ExpressFile): ExpressRole | null {
  return roleFromName(file.name) ?? roleFromSize(file.width, file.height);
}

/** The filename without its extension and without a trailing role word. */
export function stemOf(name: string): string {
  const base = stripExt(name).trim();
  if (roleFromName(name) === null) return base;
  return base.replace(/[\s_.-]+[a-z0-9-]+$/i, '').trim() || base;
}

const norm = (s: string) => s.trim().toLowerCase();

/**
 * Pair files. 1) by stem: a stem's Feed images pair with its Story images in order.
 * 2) leftovers by order: the i-th Feed image still without a Story pairs with the i-th unmatched
 * Story image. Feed images with no Story are kept ("no story version"); Story images with no
 * Feed image are returned unassigned, never dropped silently.
 */
export function autoPair(files: ExpressFile[]): PairingResult {
  const feeds: ExpressFile[] = [];
  const stories: ExpressFile[] = [];
  for (const f of files) (roleOf(f) === 'story' ? stories : feeds).push(f);

  const pairs = new Map<string, ExpressPair>(); // keyed by feed id, in upload order
  for (const f of feeds) pairs.set(f.id, { id: f.id, stem: stemOf(f.name), feedId: f.id, storyId: null });

  const used = new Set<string>();
  const feedsByStem = new Map<string, ExpressFile[]>();
  for (const f of feeds) {
    const k = norm(stemOf(f.name));
    feedsByStem.set(k, [...(feedsByStem.get(k) ?? []), f]);
  }
  const storiesByStem = new Map<string, ExpressFile[]>();
  for (const s of stories) {
    const k = norm(stemOf(s.name));
    storiesByStem.set(k, [...(storiesByStem.get(k) ?? []), s]);
  }
  for (const [stem, ss] of storiesByStem) {
    const fs = feedsByStem.get(stem) ?? [];
    ss.forEach((s, i) => {
      const f = fs[i];
      if (!f) return;
      pairs.get(f.id)!.storyId = s.id;
      used.add(s.id);
    });
  }

  // By order: Feed images still without a Story take the leftover Story images in upload order.
  const bare = feeds.filter((f) => pairs.get(f.id)!.storyId === null);
  const left = stories.filter((s) => !used.has(s.id));
  const unassigned: string[] = [];
  left.forEach((s, i) => {
    const f = bare[i];
    if (f) {
      pairs.get(f.id)!.storyId = s.id;
      used.add(s.id);
    } else {
      unassigned.push(s.id);
    }
  });
  return { pairs: [...pairs.values()], unassignedStoryIds: unassigned };
}

/** Put a Story image on a pair (moving it off any pair that had it). */
export function assignStory(result: PairingResult, storyId: string, pairId: string | null): PairingResult {
  const pairs = result.pairs.map((p) => (p.storyId === storyId ? { ...p, storyId: null } : p));
  const unassigned = result.unassignedStoryIds.filter((id) => id !== storyId);
  if (pairId === null) return { pairs, unassignedStoryIds: [...unassigned, storyId] };
  const target = pairs.find((p) => p.id === pairId);
  if (!target) return result;
  // Whatever Story the target pair had goes back to unassigned (nothing is dropped).
  const displaced = target.storyId;
  const next = pairs.map((p) => (p.id === pairId ? { ...p, storyId } : p));
  return { pairs: next, unassignedStoryIds: displaced ? [...unassigned, displaced] : unassigned };
}

export function pairHasNoStory(p: ExpressPair): boolean {
  return p.storyId === null;
}

// ---------- ad names ----------

/** "Image - Week one" -> "Image"; a name with no " - " -> "Image". */
export function namingPrefix(templateName: string | null | undefined): string {
  const m = (templateName ?? '').match(/^(.{1,24}?)\s+-\s+/);
  return m?.[1]?.trim() || 'Image';
}

export function defaultAdName(templateName: string | null | undefined, stem: string): string {
  return `${namingPrefix(templateName)} - ${stem.trim()}`;
}

export function applyAffixes(name: string, affixes: { prefix?: string; suffix?: string }): string {
  return `${affixes.prefix ?? ''}${name}${affixes.suffix ?? ''}`.trim();
}

// ---------- the batch ----------

export type ExpressStatus = 'PAUSED' | 'ACTIVE';

export interface ExpressItem {
  key: string;
  adSetId: string;
  pairId: string;
  name: string;
  noStory: boolean;
}

export const EXPRESS_MAX_ADS = 60;

/** One ad per chosen ad set per pair, ad-set-major so a stop leaves whole ad sets done. */
export function planExpressBatch(args: {
  adSetIds: string[];
  pairs: ExpressPair[];
  names: Record<string, string>;
}): ExpressItem[] {
  const out: ExpressItem[] = [];
  const seen = new Set<string>();
  for (const adSetId of args.adSetIds) {
    if (seen.has(adSetId)) continue;
    seen.add(adSetId);
    for (const p of args.pairs) {
      out.push({
        key: `${adSetId}|${p.id}`,
        adSetId,
        pairId: p.id,
        name: (args.names[p.id] ?? '').trim() || p.stem,
        noStory: p.storyId === null,
      });
    }
  }
  return out;
}

export function batchIssue(items: ExpressItem[]): string | null {
  if (items.length === 0) return 'Choose at least one ad set and add at least one image.';
  if (items.length > EXPRESS_MAX_ADS) {
    return `That is ${items.length} ads. Up to ${EXPRESS_MAX_ADS} at a time; split it into two batches.`;
  }
  if (items.some((i) => i.name === '')) return 'Every ad needs a name.';
  return null;
}

// ---------- progress ----------

export type ItemStatus = 'queued' | 'creating' | 'created' | 'failed';
export interface ItemProgress {
  status: ItemStatus;
  adId?: string | null;
  draftId?: string | null;
  error?: string | null;
}
export type Progress = Record<string, ItemProgress>;

export function initialProgress(items: ExpressItem[]): Progress {
  return Object.fromEntries(items.map((i) => [i.key, { status: 'queued' as ItemStatus }]));
}

/** Retry runs ONLY the failed items; created ones are never sent again. */
export function itemsToRun(items: ExpressItem[], progress: Progress): ExpressItem[] {
  return items.filter((i) => {
    const s = progress[i.key]?.status;
    return s === 'queued' || s === 'failed';
  });
}

export function progressCounts(progress: Progress): Record<ItemStatus, number> {
  const c: Record<ItemStatus, number> = { queued: 0, creating: 0, created: 0, failed: 0 };
  for (const p of Object.values(progress)) c[p.status] += 1;
  return c;
}

/** Newest active ad in the ad set (the default template); falls back to the newest of any status. */
export function defaultTemplateAdId(
  ads: Array<{ id: string; metaStatus: string | null; platformCreatedAt: string | null }>,
): string | null {
  const ts = (a: { platformCreatedAt: string | null }) => (a.platformCreatedAt ? Date.parse(a.platformCreatedAt) : 0) || 0;
  const newest = (xs: typeof ads) => [...xs].sort((a, b) => ts(b) - ts(a))[0]?.id ?? null;
  const active = ads.filter((a) => String(a.metaStatus ?? '').toUpperCase() === 'ACTIVE');
  return newest(active) ?? newest(ads);
}

/** Active ad sets are the default selection. */
export function defaultAdSetIds(adSets: Array<{ id: string; metaStatus: string | null }>): string[] {
  return adSets.filter((s) => String(s.metaStatus ?? '').toUpperCase() === 'ACTIVE').map((s) => s.id);
}
